// Web-side bridge client. No-ops entirely when NOT running inside the native shell,
// so the student-app behaves identically as a plain website.
// Design: docs/FeatureDesigns/hybrid-shell-chrome-contract.md §5.
//
// Transport:
//   web → native : iOS window.webkit.messageHandlers.<name>.postMessage(payload)
//                  android window.AndroidShell.postMessage(JSON string)  [later]
//   native → web : native calls window.__shellNavigate(path) / evaluateJavaScript

import type { ChromeDescriptor, TabModel } from './chrome';

export interface NativeShellInfo {
  platform: 'ios' | 'android';
  shellVersion: string;
  bridgeVersion: number;
  /** 'native' when the shell renders the First-Run pick screen itself (web skips
   *  its own PickToStart and consumes the picks the shell hands over). */
  firstRun?: 'native';
}

/** The exam/class/subject the native First-Run screen collected. */
export interface ShellFrePick {
  exam: string;    // canonical Programme (JEE / NEET / CBSE)
  subject: string; // lowercase canonical subject key
  grade: number;   // class number
}

declare global {
  interface Window {
    __NATIVE_SHELL__?: NativeShellInfo;
    __shellNavigate?: (path: string) => void;
    // Native First-Run hand-off: the shell loads /?shell_fre=1&exam=&subject=&grade=;
    // ShellSync reads the params and sets this for FirstRun to consume.
    __shellFrePick?: ShellFrePick;
    // Native Sign in with Apple result callback (set by requestAppleSignIn).
    __shellAppleResult?: (result: ShellAppleCredential | { error: string }) => void;
    // Native Google sign-in result callback (set by requestGoogleSignIn).
    __shellGoogleResult?: (result: { idToken: string } | { error: string }) => void;
    // Native canvas async op result (export/serialize/restore/rasterize), keyed by
    // requestId. Installed lazily by the canvas bridge.
    __shellCanvasResult?: (
      requestId: number,
      result: { value: string | null } | { error: string },
    ) => void;
    // Native canvas content-changed push — keeps the web mirror (isEmpty + last
    // raster) warm so the synchronous DrawingCanvasRef methods can answer locally.
    __shellCanvasEvent?: (event: CanvasContentEvent) => void;
    // Native push: device token (or error) after the shell registers with APNs.
    __shellPushToken?: (result: ShellPushToken | { error: string }) => void;
    // Native push: current permission state, in response to push_permission_state
    // or after a permission prompt resolves.
    __shellPushPermission?: (result: { state: PushPermissionState }) => void;
    // Native canvas tool-picker obscured inset (CSS px): how much of the viewport
    // bottom the floating PencilKit tool picker covers, so the web can reserve that
    // space and keep the solve footer (Analyze) reachable. 0 when nothing obscured.
    __shellCanvasInset?: (bottomPx: number) => void;
    webkit?: {
      messageHandlers?: Record<string, { postMessage: (msg: unknown) => void }>;
    };
    AndroidShell?: { postMessage: (msg: string) => void };
  }
}

/** True when the native shell owns the First-Run pick step. */
export function isNativeFirstRun(): boolean {
  return shellInfo()?.firstRun === 'native';
}

/** Read the picks the shell delivered (set by ShellSync from the URL params). */
export function consumeShellFrePick(): ShellFrePick | undefined {
  return typeof window !== 'undefined' ? window.__shellFrePick : undefined;
}

/**
 * Ask the native shell to run Sign in with Apple and resolve with Apple's identity
 * token (+ name on first sign-in). The web then POSTs it to /auth/apple. Rejects if
 * not in the shell, or on cancel/error.
 */
export function requestAppleSignIn(): Promise<ShellAppleCredential> {
  return new Promise((resolve, reject) => {
    if (!isInShell()) {
      reject(new Error('Apple sign-in is only available in the app'));
      return;
    }
    window.__shellAppleResult = (result) => {
      window.__shellAppleResult = undefined;
      if (result && 'identityToken' in result && result.identityToken) {
        resolve(result);
      } else {
        reject(new Error((result as { error?: string })?.error || 'Apple sign-in failed'));
      }
    };
    postToNative('auth_apple', {});
  });
}

/**
 * Tell the shell the native First-Run hand-off has reached the solve screen, so it
 * can crossfade from the native FRE screen to the WebView. Until this fires the
 * shell keeps the native screen up (spinner) — the guest bootstrap + /first-run
 * load stay hidden, so the marketing Landing never flashes.
 */
export function signalFreReady(): void {
  postToNative('fre_ready', {});
}

/**
 * Ask the native shell to run Google sign-in (system browser, not the embedded
 * WebView, which Google blocks) and resolve with Google's id_token. The web then
 * POSTs it to /auth/google with clientType 'ios'. Rejects if not in the shell, or
 * on cancel/error.
 */
export function requestGoogleSignIn(): Promise<{ idToken: string }> {
  return new Promise((resolve, reject) => {
    if (!isInShell()) {
      reject(new Error('Google sign-in is only available in the app'));
      return;
    }
    window.__shellGoogleResult = (result) => {
      window.__shellGoogleResult = undefined;
      if (result && 'idToken' in result && result.idToken) {
        resolve(result);
      } else {
        reject(new Error((result as { error?: string })?.error || 'Google sign-in failed'));
      }
    };
    postToNative('auth_google', {});
  });
}

/** Device identity the native shell hands over after registering with APNs. */
export interface ShellPushToken {
  token: string;
  installId: string;
  platform: 'ios' | 'android';
  /** Which APNs environment minted the token — the backend cannot infer it. */
  apnsEnv: 'sandbox' | 'production';
  bundleId: string;
  appVersion?: string;
  osVersion?: string;
  deviceModel?: string;
}

export type PushPermissionState = 'granted' | 'denied' | 'undetermined';

/**
 * Ask the shell to request notification permission and register with APNs.
 *
 * Call this only AFTER the user has seen value (first completed session, first
 * duel sent) — iOS shows the system prompt exactly once and a denial is
 * effectively permanent, so a cold-launch prompt burns the only attempt.
 *
 * Resolves with the device identity for the caller to POST to /push/devices.
 * Registration is done by web rather than native because web owns the session:
 * sign-in is delegated to native but the bearer token never leaves web.
 * Rejects outside the shell, on denial, or on APNs failure.
 */
export function requestPushRegistration(): Promise<ShellPushToken> {
  return new Promise((resolve, reject) => {
    if (!isInShell()) {
      reject(new Error('Push notifications are only available in the app'));
      return;
    }
    pendingPushRequest = { resolve, reject };
    installPushTokenHandler();
    postToNative('push_register', {});
  });
}

/**
 * Subscribe to device tokens the shell delivers WITHOUT the web having asked.
 *
 * The shell silently re-registers on every launch when permission is already
 * granted, so a token can arrive with no promise waiting for it. Without a
 * durable handler that token is dropped on the floor — and since registration
 * is the only moment the backend hears about an install, dropping it means
 * stale server-side device state never gets repaired.
 *
 * Returns an unsubscribe function. No-ops outside the shell.
 */
export function onPushToken(cb: (token: ShellPushToken) => void): () => void {
  if (!isInShell()) return () => {};
  pushTokenSubscribers.add(cb);
  installPushTokenHandler();
  return () => { pushTokenSubscribers.delete(cb); };
}

const pushTokenSubscribers = new Set<(token: ShellPushToken) => void>();
let pendingPushRequest: {
  resolve: (t: ShellPushToken) => void;
  reject: (e: Error) => void;
} | null = null;

/**
 * One durable native→web entry point, fanning out to whoever is listening.
 * Installed once: a per-call handler would be clobbered by the next caller and
 * would miss unsolicited tokens entirely.
 */
function installPushTokenHandler(): void {
  if (window.__shellPushToken) return;
  window.__shellPushToken = (result) => {
    const pending = pendingPushRequest;
    pendingPushRequest = null;

    if (result && 'token' in result && result.token) {
      pending?.resolve(result);
      for (const cb of pushTokenSubscribers) {
        try { cb(result); } catch { /* one bad subscriber must not stop the rest */ }
      }
    } else {
      pending?.reject(new Error((result as { error?: string })?.error || 'Push registration failed'));
    }
  };
}

/**
 * Current notification permission, WITHOUT prompting. Use this to drive settings
 * UI and to decide whether asking is still possible ('undetermined') or the user
 * must be sent to iOS Settings ('denied'). Resolves 'undetermined' outside the shell.
 */
export function getPushPermissionState(): Promise<PushPermissionState> {
  return new Promise((resolve) => {
    if (!isInShell()) {
      resolve('undetermined');
      return;
    }
    const timeout = window.setTimeout(() => {
      window.__shellPushPermission = undefined;
      resolve('undetermined');
    }, 3000);

    window.__shellPushPermission = (result) => {
      window.clearTimeout(timeout);
      window.__shellPushPermission = undefined;
      resolve(result?.state ?? 'undetermined');
    };
    postToNative('push_permission_state', {});
  });
}

/** Clear the app icon badge. No-op outside the shell. */
export function clearPushBadge(): void {
  postToNative('push_clear_badge', {});
}

export function shellInfo(): NativeShellInfo | undefined {
  return typeof window !== 'undefined' ? window.__NATIVE_SHELL__ : undefined;
}

/** True when the app is running inside the native shell (vs a plain browser). */
export function isInShell(): boolean {
  return !!shellInfo();
}

type NativeMessage =
  | 'chrome_set'
  | 'set_tabs'
  | 'navigate'
  | 'fre_ready'
  | 'auth_apple'
  | 'auth_google'
  | 'debug_reset'
  | 'canvas_mount'
  | 'canvas_unmount'
  | 'canvas_set_rect'
  | 'canvas_set_tool'
  | 'canvas_clear'
  | 'canvas_export'
  | 'canvas_serialize'
  | 'canvas_restore'
  | 'canvas_rasterize'
  | 'push_register'
  | 'push_permission_state'
  | 'push_clear_badge';

/**
 * QA/debug: ask the native shell to wipe all app data — the native First-Run flag
 * plus web storage/cookies — and relaunch into First-Run, i.e. simulate a fresh
 * install without deleting the app. No-op outside the shell.
 */
export function resetAppData(): void {
  postToNative('debug_reset', {});
}

/** Apple identity token (+ name on first sign-in) returned by the native shell. */
export interface ShellAppleCredential {
  identityToken: string;
  givenName?: string;
  familyName?: string;
}

function postToNative(name: NativeMessage, payload: Record<string, unknown>): void {
  const info = shellInfo();
  if (!info) return; // plain web → no-op
  try {
    if (info.platform === 'ios') {
      window.webkit?.messageHandlers?.[name]?.postMessage(payload);
    } else if (info.platform === 'android') {
      window.AndroidShell?.postMessage(JSON.stringify({ name, payload }));
    }
  } catch {
    /* never let a bridge post break the web app */
  }
}

/** Push the current screen's chrome descriptor to the native shell. */
export function setChrome(descriptor: ChromeDescriptor): void {
  postToNative('chrome_set', descriptor as unknown as Record<string, unknown>);
}

/**
 * Report the shell's tab model (membership, order, labels) to the native shell,
 * which rebuilds its tab bar to match — mapping each stable `id` to a native
 * icon. Lets conditional tabs (e.g. Assigned for teacher-linked students) track
 * the live web IA instead of a baked-in native list. No-op on plain web.
 */
export function setTabs(tabs: TabModel[]): void {
  postToNative('set_tabs', { tabs } as unknown as Record<string, unknown>);
}

export interface NavigateIntent {
  intent: 'push' | 'replace' | 'pop' | 'popToRoot';
  path?: string;
  tab?: string;
}

/** Ask the native shell to update its navigation stack (web-initiated nav). */
export function navigate(intent: NavigateIntent): void {
  postToNative('navigate', intent as unknown as Record<string, unknown>);
}

/**
 * Register the SPA's navigate function so native can drive routes via
 * window.__shellNavigate(path). Returns an unregister cleanup.
 */
export function registerNavigator(fn: (path: string) => void): () => void {
  if (typeof window === 'undefined') return () => {};
  window.__shellNavigate = (path: string) => {
    try {
      fn(path);
    } catch {
      /* ignore */
    }
  };
  return () => {
    if (typeof window !== 'undefined') delete window.__shellNavigate;
  };
}

// ── Native canvas (PencilKit) bridge ──────────────────────────────────────
// The web owns the DrawingCanvasRef contract; the native module answers
// asynchronously over the bridge. `NativeCanvas.tsx` keeps a local mirror
// (isEmpty + last raster), warmed by canvas content events, so the *synchronous*
// ref methods can answer without blocking on the bridge. Design:
// docs/FeatureDesigns/native-canvas-plan.md.

/** On-screen rect (CSS px, viewport-relative) the native canvas overlays. */
export interface CanvasRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface CanvasMountOpts {
  rect: CanvasRect;
  /** Logical drawing area the native canvas scrolls within (matches Fabric). */
  logicalWidth: number;
  logicalHeight: number;
  tool?: string;
}

/** Pushed by native after each stroke (debounced) to keep the web mirror warm,
 *  so the synchronous DrawingCanvasRef methods (isEmpty/exportImage/getJSON) can
 *  answer without a bridge round-trip. */
export interface CanvasContentEvent {
  isEmpty: boolean;
  /** Latest raster (base64 PNG, no data-URL prefix) for the sync exportImage(). */
  png?: string;
  /** Latest opaque PencilKit serialization (base64) for the sync getJSON(). */
  blob?: string;
}

let canvasReqSeq = 0;
const canvasPending = new Map<
  number,
  { resolve: (v: string | null) => void; reject: (e: Error) => void }
>();
let canvasContentCb: ((event: CanvasContentEvent) => void) | undefined;
let canvasInsetCb: ((bottomPx: number) => void) | undefined;

function ensureCanvasCallbacks(): void {
  if (typeof window === 'undefined') return;
  if (!window.__shellCanvasResult) {
    window.__shellCanvasResult = (requestId, result) => {
      const pending = canvasPending.get(requestId);
      if (!pending) return;
      canvasPending.delete(requestId);
      if (result && 'value' in result) pending.resolve(result.value);
      else pending.reject(new Error((result as { error?: string })?.error || 'canvas op failed'));
    };
  }
  if (!window.__shellCanvasEvent) {
    window.__shellCanvasEvent = (event) => {
      try {
        canvasContentCb?.(event);
      } catch {
        /* never let a native push break the web app */
      }
    };
  }
  if (!window.__shellCanvasInset) {
    window.__shellCanvasInset = (bottomPx) => {
      try {
        canvasInsetCb?.(bottomPx);
      } catch {
        /* never let a native push break the web app */
      }
    };
  }
}

function canvasRequest(name: NativeMessage, payload: Record<string, unknown>): Promise<string | null> {
  return new Promise((resolve, reject) => {
    if (!isInShell()) {
      reject(new Error('native canvas is only available in the app'));
      return;
    }
    ensureCanvasCallbacks();
    const requestId = ++canvasReqSeq;
    canvasPending.set(requestId, { resolve, reject });
    postToNative(name, { ...payload, requestId });
  });
}

/**
 * Native PencilKit canvas control surface. `supported()` gates on the shell
 * advertising bridge v2+, so an older shell that predates the canvas module
 * cleanly falls back to the Fabric engine on the web side.
 */
export const canvas = {
  /** True when the running shell exposes the native canvas module (bridge v2+). */
  supported(): boolean {
    const info = shellInfo();
    return !!info && info.platform === 'ios' && info.bridgeVersion >= 2;
  },
  mount(opts: CanvasMountOpts): void {
    postToNative('canvas_mount', opts as unknown as Record<string, unknown>);
  },
  unmount(): void {
    postToNative('canvas_unmount', {});
  },
  /** Reposition the overlay when the web canvas region moves (resize/scroll). */
  setRect(rect: CanvasRect): void {
    postToNative('canvas_set_rect', { rect } as unknown as Record<string, unknown>);
  },
  setTool(tool: string): void {
    postToNative('canvas_set_tool', { tool });
  },
  clear(): void {
    postToNative('canvas_clear', {});
  },
  /** Rasterize the *displayed* drawing → base64 PNG (bbox-cropped, 2×). */
  exportImage(): Promise<string | null> {
    return canvasRequest('canvas_export', {});
  },
  /** Opaque PencilKit serialization (base64) for durable drafts (Option A). */
  serialize(): Promise<string | null> {
    return canvasRequest('canvas_serialize', {});
  },
  /** Restore a previously serialized drawing into the displayed canvas. */
  restore(blob: string): Promise<string | null> {
    return canvasRequest('canvas_restore', { blob });
  },
  /** Rasterize a serialized (off-screen) drawing → base64 PNG, without mounting.
   *  Used by the End-Test / Save-&-Exit batch path for questions not on screen. */
  rasterize(blob: string): Promise<string | null> {
    return canvasRequest('canvas_rasterize', { blob });
  },
  /** Subscribe to native content-changed pushes (one active subscriber). */
  onContent(cb: (event: CanvasContentEvent) => void): () => void {
    ensureCanvasCallbacks();
    canvasContentCb = cb;
    return () => {
      if (canvasContentCb === cb) canvasContentCb = undefined;
    };
  },
  /** Subscribe to native tool-picker obscured-inset pushes (CSS px at the bottom).
   *  One active subscriber; fires 0 on unmount. */
  onInset(cb: (bottomPx: number) => void): () => void {
    ensureCanvasCallbacks();
    canvasInsetCb = cb;
    return () => {
      if (canvasInsetCb === cb) canvasInsetCb = undefined;
    };
  },
};
